import { createContext, useContext, useState, useRef, useEffect } from 'react'
import AutoCapture from '../../components/camera/AutoCapture'
import { useLocation, matchPath } from 'react-router-dom'

const CameraContext = createContext()
export const useCameraContext = () => useContext(CameraContext)

export const CameraProvider = ({ children }) => {
  const [permissionGranted, setPermissionGranted] = useState(null)
  const [isCapturing, setIsCapturing] = useState(false)
  const [waitForMount, setWaitForMount] = useState(false)
  const [moduleId, setModuleId] = useState(null)
  const autoCaptureRef = useRef()

  const location = useLocation()
  const isQuestionPage = matchPath({ path: '/question/:id' }, location.pathname)
  const isQuestionPageRef = useRef(isQuestionPage)

  // console.log(isQuestionPage)

  useEffect(() => {
    isQuestionPageRef.current = isQuestionPage
  }, [isQuestionPage])

  // Auto check permission saat mount (dengan fallback Safari)
  useEffect(() => {
    const checkPermission = async () => {
      if (!navigator.permissions) {
        console.warn('Permissions API tidak disupport, fallback ke getUserMedia...')
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true })
          stream.getTracks().forEach((t) => t.stop())
          setPermissionGranted(true)
        } catch (err) {
          console.warn('Kamera tidak diizinkan (fallback Safari):', err)
          setPermissionGranted(false)
        }
        return
      }

      try {
        const result = await navigator.permissions.query({ name: 'camera' })

        if (result.state === 'granted') setPermissionGranted(true)
        else if (result.state === 'denied') setPermissionGranted(false)
        else setPermissionGranted(null)

        // Real-time update kalau user ubah izin di browser
        result.onchange = async () => {
          // console.log('[Camera Permission Changed]', result.state)

          if (result.state === 'granted') {
            setPermissionGranted(true)
            // Restart kamera otomatis begitu diizinkan lagi
            if (isQuestionPageRef.current && autoCaptureRef.current) {
              await autoCaptureRef.current.startCamera()
              setIsCapturing(true)
            }
          } else if (result.state === 'denied') {
            setPermissionGranted(false)
            // Stop kamera begitu dicabut

            if (autoCaptureRef.current) {
              autoCaptureRef.current.stopCamera()
              setIsCapturing(false)
            }
          } else {
            setPermissionGranted(null)
          }
        }
      } catch (err) {
        console.error('Gagal ngecek permission kamera:', err)
        // fallback kalau query gagal
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true })
          stream.getTracks().forEach((t) => t.stop())
          setPermissionGranted(true)
        } catch (error) {
          setPermissionGranted(false)
        }
      }
    }

    checkPermission()
  }, [])

  useEffect(() => {
    if (!isQuestionPage) {
      autoCaptureRef.current?.stopCamera()
      setIsCapturing(false)
    }
  }, [isQuestionPage])

  // Minta izin manual
  const requestPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true })
      stream.getTracks().forEach((t) => t.stop())
      setPermissionGranted(true)
    } catch (err) {
      console.error('Permission kamera ditolak:', err)
      setPermissionGranted(false)
    }
  }

  // Start Capture (dengan retry & fallback)
  const startCapture = async (retry = 0) => {
    if (retry > 5) {
      console.warn('Gagal start kamera setelah 5x percobaan')
      return
    }

    let granted = permissionGranted

    if (granted === null) {
      try {
        if (navigator.permissions) {
          const result = await navigator.permissions.query({ name: 'camera' })
          granted = result.state === 'granted'
        } else {
          // fallback Safari
          const stream = await navigator.mediaDevices.getUserMedia({ video: true })
          stream.getTracks().forEach((t) => t.stop())
          granted = true
        }
      } catch (err) {
        console.warn('Gagal query / getUserMedia:', err)
        granted = false
      }
      setPermissionGranted(granted)
    }

    if (!granted) {
      console.warn('Kamera belum diizinkan')
      return
    }

    // if (!autoCaptureRef.current || !moduleId) {
    //   console.warn('Skip startCapture — belum ada moduleId.')
    //   return
    // }

    if (autoCaptureRef.current) {
      await autoCaptureRef.current.startCamera()
      setIsCapturing(true)
    } else {
      setWaitForMount(true)
    }
  }

  // Stop kamera
  const stopCapture = () => {
    autoCaptureRef.current?.stopCamera()
    setIsCapturing(false)
  }

  // NEW MECHANISM, capture once
  const startCameraSession = async () => {
    console.log('🎥 CAMERA SESSION STARTED')
    if (!permissionGranted) {
      console.warn('🎥 Kamera belum diizinkan')
      return
    }

    if (!autoCaptureRef.current) {
      console.warn('🎥 AutoCapture belum siap')
      return
    }

    if (autoCaptureRef.current) {
      await autoCaptureRef.current.startCamera()
      setIsCapturing(true)
    } else {
      setWaitForMount(true)
    }
    console.log({ isCapturing })
  }

  const captureOnce = async (questionNumber) => {
    if (!permissionGranted) {
      console.warn('📸 Skip captureOnce — kamera belum diizinkan')
      return
    }

    if (!autoCaptureRef.current) {
      console.warn('📸 AutoCapture ref belum siap')
      return
    }

    try {
      return await autoCaptureRef.current.captureOnce(questionNumber)
    } catch (err) {
      console.error('📸 Gagal captureOnce:', err)
      return 'failed'
    }
  }

  const requestCameraPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true })
      stream.getTracks().forEach((t) => t.stop())
      setPermissionGranted(true)
      return true
    } catch {
      setPermissionGranted(false)
      return false
    }
  }

  // Kalau AutoCapture baru mount setelah startCapture
  useEffect(() => {
    if (waitForMount && autoCaptureRef.current && permissionGranted) {
      autoCaptureRef.current.startCamera()
      setIsCapturing(true)
      setWaitForMount(false)
    }
  }, [waitForMount, permissionGranted])

  return (
    <CameraContext.Provider
      value={{
        permissionGranted,
        isCapturing,
        requestPermission,
        startCapture,
        stopCapture,
        setModuleId,
        captureOnce,
        startCameraSession,
        requestCameraPermission,
      }}
    >
      {children}
      {/* <AutoCapture ref={autoCaptureRef} /> */}
      {/* <AutoCapture ref={autoCaptureRef} moduleId={moduleId} /> */}
      <AutoCapture ref={autoCaptureRef} moduleId={moduleId} />
    </CameraContext.Provider>
  )
}
